import './AgentChallengePage.css'

import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useSearchParams } from 'react-router-dom'

import { useLogger } from '../logger'

const KNOWN_AGENTS: Record<string, { displayName: string; thumbnailUrl?: string }> = {
  alex_jones: {
    displayName: 'Alex Jones',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/alex_jones/thumbnail.jpg',
  },
  andrew_tate: {
    displayName: 'Andrew Tate',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/andrew_tate/thumbnail.jpg',
  },
  candace_owens: {
    displayName: 'Candace Owens',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/candace_owens/thumbnail.jpg',
  },
  hasan_piker: {
    displayName: 'Hasan Piker',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/hasan_piker/thumbnail.jpg',
  },
  jordan_peterson: {
    displayName: 'Jordan Peterson',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/jordan_peterson/thumbnail.jpg',
  },
  nick_fuentes: {
    displayName: 'Nick Fuentes',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/nick_fuentes/thumbnail.jpg',
  },
  tucker_carlson: {
    displayName: 'Tucker Carlson',
    thumbnailUrl: 'https://d1yrgwskeg1le6.cloudfront.net/agents/tucker_carlson/thumbnail.jpg',
  },
}

function formatAgentId(agentId: string): string {
  return agentId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export default function AgentChallengePage() {
  const { agentId } = useParams<{ agentId: string }>()
  const [searchParams] = useSearchParams()
  const ct = searchParams.get('ct')
  const logger = useLogger()

  const known = agentId ? KNOWN_AGENTS[agentId] : undefined
  const agent = known
    ? { agentId: agentId!, ...known }
    : { agentId: agentId || '', displayName: formatAgentId(agentId || ''), thumbnailUrl: undefined }

  const handleAcceptChallenge = () => {
    const deepLink = ct ? `nugle://agent/${agentId}?ct=${ct}` : `nugle://agent/${agentId}`
    const appStoreUrl = 'https://apps.apple.com/app/nugle-be-heard/id6745102771'

    logger.info('Attempting agent deep link', { deepLink })

    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    document.body.appendChild(iframe)

    const timeout = setTimeout(() => {
      logger.info('App not installed, redirecting to App Store')
      window.location.href = appStoreUrl
    }, 2000)

    iframe.onload = () => {
      clearTimeout(timeout)
      document.body.removeChild(iframe)
    }

    iframe.src = deepLink
    window.location.href = deepLink
  }

  const displayName = agent.displayName || agentId || 'an AI opponent'

  return (
    <div className="challenge-container">
      <Helmet>
        <title>{`Debate ${displayName} on Nugle`}</title>
        <meta name="description" content={`You've been challenged to debate ${displayName}. Think you can win?`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Debate Challenge: ${displayName}`} />
        <meta
          property="og:description"
          content={`You've been challenged to debate ${displayName} on Nugle. Think you can win?`}
        />
        <meta property="og:image" content={`${window.location.origin}/nugle-circle.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Debate Challenge: ${displayName}`} />
        <meta
          name="twitter:description"
          content={`You've been challenged to debate ${displayName} on Nugle. Think you can win?`}
        />
      </Helmet>

      <div className="challenge-content">
        <div className="challenge-card">
          {agent?.thumbnailUrl ? (
            <img src={agent.thumbnailUrl} alt={displayName} className="agent-avatar" />
          ) : (
            <div className="agent-avatar-placeholder">{displayName.charAt(0).toUpperCase()}</div>
          )}

          <h1 className="agent-name">{displayName}</h1>

          <p className="challenge-text">
            You&apos;ve been challenged to debate <strong>{displayName}</strong>.
            <br />
            Think you can win?
          </p>

          <button className="accept-button" onClick={handleAcceptChallenge}>
            Accept Challenge
          </button>
        </div>

        <div className="nugle-branding">NUGLE</div>
      </div>
    </div>
  )
}
